import React, { useCallback, useMemo, useRef, useState } from "react";
import type {
  RollupWithTagsAndCountsAndRange,
  Process,
} from "../../types/types";
import {
  useGetPageFromPath,
  useShouldMockShiftEnd,
  useFocusRange,
  useSelectedProcesses,
  useDashboardPageContext,
} from "../../utils/customHooks/genericHooks";
import {
  getDayStart,
  ONE_DAY_OF_MS,
  ONE_MINUTE_OF_MS,
} from "../../utils/timeUtils";
import { useSettings } from "../../contexts/SettingsManager/SettingsManager";
import { AGGREGATE_NO_FREQUENCY_QUERY } from "./aggregatesGetterUtils";
import { AggregateGetterSingle } from "./AggregateGetterSingle";
import { AggregateGetterTrendChartBars } from "./AggregateGetterTrendChartBarsProps";
import {
  AGGREGATES_POLL_INTERVAL,
  AGGREGATES_POLL_INTERVAL_TRENDS_PAGE,
  FAST_RETRY_INTERVAL,
} from "../../constants/intervals";
import {
  AGGREGATE_FOR_FOCUS_RANGE_QUERY_TITLE,
  AGGREGATES_FOR_TREND_CHART_BARS_QUERY_TITLE,
} from "contexts/ErrorManager/errorManagerUtils";
import { isEqual } from "lodash";
import { useSelector } from "store/useSelector";
import { useShouldUseDataDumpsForTrendChartBarstacks } from "utils/customHooks/whichServerHooks";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { addRollupValues } from "utils/metrics";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useIsTrendChartExpandedTimelinePage } from "pages/DashboardPages/dashboardPagesUtils";

const EMPTY_DISPATCH = { type: null, payload: null };

const MAX_FAST_RETRIES_IN_A_ROW = 10;

type AllAggregatesGettersRolluperProps = {
  process: Process;
  queueRollupForDispatch: (rollup: RollupWithTagsAndCountsAndRange) => void;
};
const AllAggregatesGettersRolluper = ({
  process,
  queueRollupForDispatch,
}: // tslint:disable-next-line: no-big-function
AllAggregatesGettersRolluperProps) => {
  const focusRange = useFocusRange();
  const { isTimelinePage, isGroupTilesPage, isHubletPage, isTrendsPage } =
    useGetPageFromPath();
  const shouldMockShiftEnd = useShouldMockShiftEnd();
  const isTrendsPageFinishedInitialFilter = useSelector(
    state => state.legacy.isTrendsPageFinishedInitialFilter
  );
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  const initialPollInterval =
    multiplyNetworkRequestIntervals *
    (shouldMockShiftEnd
      ? 10 * 1000
      : isTrendsPage
        ? AGGREGATES_POLL_INTERVAL_TRENDS_PAGE
        : AGGREGATES_POLL_INTERVAL);

  const [pollInterval, setPollInterval] = useState(initialPollInterval);

  const selectedProcessIdsRef = useSelectedProcesses();

  const shouldFetchAggregates =
    // (need them all on Trends, Details, Group Tiles, Timeline pages)
    isTrendsPage ||
    isHubletPage ||
    isGroupTilesPage ||
    isTimelinePage ||
    // on Grid or ProcessTiles pages, only need aggregates for selected processes (do need all on Timeline page for "Other Processes" chart)
    selectedProcessIdsRef.current.includes(process.id);

  if (!focusRange || !process || !shouldFetchAggregates) {
    return null;
  }

  const isTrendsPageReady = !isTrendsPage || isTrendsPageFinishedInitialFilter;

  return isTrendsPageReady ? (
    <>
      <AggregateGetterSingleWrapper
        {...{
          process,
          pollInterval,
          setPollInterval,
          initialPollInterval,
          queueRollupForDispatch,
        }}
      />
      <AggregatesGetterTrendChartBarsWrapper {...{ process, pollInterval }} />
    </>
  ) : null;
};

/* this <AggregateGetter/> queries for a single aggregate for the full focus range */
function AggregateGetterSingleWrapper({
  process,
  pollInterval,
  setPollInterval,
  initialPollInterval,
  queueRollupForDispatch,
}: {
  process: Process;
  pollInterval: number;
  setPollInterval: React.Dispatch<React.SetStateAction<number>>;
  initialPollInterval: number;
  queueRollupForDispatch: (rollup: RollupWithTagsAndCountsAndRange) => void;
}) {
  const focusRange = useFocusRange();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const numFastRetriesRef = useRef(0); // infinite loop if we don't use a ref
  const {
    isProcessTilesPage,
    isTimelinePage,
    isGroupTilesPage,
    isHubletPage,
    isTrendsPage,
  } = useGetPageFromPath();
  const { dayStartMsOffset, isTilesPageFullDay } = useSettings();
  const { isFullDaySelected } = useDashboardPageContext();
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();

  const variables = useMemo(() => {
    if (!focusRange) {
      return {
        ge: "",
        lt: "",
        machemetrySourceName: "",
      };
    }
    const fullDayStart = getDayStart(
      focusRange.ge,
      dayStartMsOffset,
      timeZoneMsOffset
    );

    const fullDayRangeStrings = {
      ge: fullDayStart.toString(),
      lt: (fullDayStart + ONE_DAY_OF_MS).toString(),
    };
    const focusRangeStrings = {
      ge: focusRange.ge.toString(),
      lt: focusRange.lt.toString(),
    };
    const shouldQueryFullDayRange = // we're not on the trends page AND
      // we're in full day mode on the timeline OR hublet page OR
      isTrendsPage
        ? false
        : isTimelinePage || isHubletPage
          ? isFullDaySelected
          : // we're in full day mode on the tiles page
            isProcessTilesPage || isGroupTilesPage
            ? isTilesPageFullDay
            : false;
    const queryRange = shouldQueryFullDayRange
      ? fullDayRangeStrings
      : focusRangeStrings;
    const variables = { ...queryRange, machemetrySourceName: process.id };
    return variables;
  }, [
    dayStartMsOffset,
    focusRange,
    isFullDaySelected,
    isGroupTilesPage,
    isHubletPage,
    isProcessTilesPage,
    isTilesPageFullDay,
    isTimelinePage,
    isTrendsPage,
    process.id,
    timeZoneMsOffset,
  ]);

  const dispatchFn = useCallback(
    (rollups: RollupWithTagsAndCountsAndRange[]) => {
      const combinedRollup = addRollupValues(rollups);
      const isValidRollup = validateRollup(variables, combinedRollup);

      doFastRetryIfInvalid(
        isValidRollup,
        numFastRetriesRef,
        setPollInterval,
        initialPollInterval
      );

      return isValidRollup
        ? queueRollupForDispatch(combinedRollup)
        : EMPTY_DISPATCH;
    },
    [queueRollupForDispatch, setPollInterval, initialPollInterval, variables]
  );

  if (!focusRange) {
    return null;
  }

  return (
    <AggregateGetterSingle
      variables={variables}
      queryTitle={AGGREGATE_FOR_FOCUS_RANGE_QUERY_TITLE}
      query={AGGREGATE_NO_FREQUENCY_QUERY}
      shouldQuery={true}
      process={process}
      dispatchFn={dispatchFn}
      pollInterval={pollInterval * multiplyNetworkRequestIntervals}
    />
  );
}

function AggregatesGetterTrendChartBarsWrapper({
  process,
  pollInterval,
}: {
  process: Process;
  pollInterval: number;
}) {
  const { isProcessTilesPage, isTimelinePage, isGroupTilesPage, isTrendsPage } =
    useGetPageFromPath();

  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  const shouldUseDataDumpsForTrendChart =
    useShouldUseDataDumpsForTrendChartBarstacks();
  const [isTrendChartExpandedTimelinePage] =
    useIsTrendChartExpandedTimelinePage();

  const shouldQueryTrendChartBarsAggregate =
    !isGroupTilesPage &&
    !isProcessTilesPage &&
    // on trends page, fetch trend bar aggregates so we can show some data (even if less accurate) in filtered-by-shift state
    (isTrendsPage || !shouldUseDataDumpsForTrendChart) &&
    // on timeline page, fetch only if trend chart is expanded
    (!isTimelinePage || isTrendChartExpandedTimelinePage);

  return shouldQueryTrendChartBarsAggregate ? (
    // this <AggregateGetter /> queries for the Trend Chart aggregates (e.g. 1 aggregate per hour)
    <AggregateGetterTrendChartBars
      queryTitle={AGGREGATES_FOR_TREND_CHART_BARS_QUERY_TITLE}
      shouldQuery={shouldQueryTrendChartBarsAggregate}
      process={process}
      pollInterval={pollInterval * multiplyNetworkRequestIntervals}
    />
  ) : null;
}

export default React.memo(AllAggregatesGettersRolluper, isEqual);

// ? Qiagen DirectorOfOperations EoL is returning ~4.5 minutes difference. Still investigating, setting this as 8 minutes for now
const MAX_SERVER_LATENCY = 8 * ONE_MINUTE_OF_MS;

function doFastRetryIfInvalid(
  isValidRollup: boolean,
  numFastRetriesRef: React.MutableRefObject<number>,
  setPollInterval: React.Dispatch<React.SetStateAction<number>>,
  initialPollInterval: number
) {
  const isTooManyRetriesInARow =
    numFastRetriesRef.current > MAX_FAST_RETRIES_IN_A_ROW;

  if (!isValidRollup && !isTooManyRetriesInARow) {
    numFastRetriesRef.current++;
    setPollInterval(FAST_RETRY_INTERVAL);
  } else {
    numFastRetriesRef.current = 0;
    setPollInterval(initialPollInterval);
  }
}

/**
 * to deal with Apollo cache issues returning the wrong rollup,
 * verify that the rollup duration matches the expected rollup duration before dispatching
 */
function validateRollup(
  queryVariables: {
    machemetrySourceName: string;
    ge: string;
    lt: string;
  },
  rollup: RollupWithTagsAndCountsAndRange
) {
  if (rollup.isFake) {
    // for testing purposes
    return true;
  }

  const queryDuration = Number(queryVariables.lt) - Number(queryVariables.ge);

  const responseDuration = rollup.totalDurationMs;

  // verify that the values aren't significantly different
  const durationDifference = responseDuration - queryDuration;
  const now = Date.now();
  const isCurrentQuery =
    Number(queryVariables.ge) < now && now < Number(queryVariables.lt); // if the query is for the current time, it can return less than the full duration and still be valid
  const isValid =
    Math.abs(durationDifference) < MAX_SERVER_LATENCY || isCurrentQuery;
  if (!isValid) {
    const formatMinutes = num => (num / ONE_MINUTE_OF_MS).toFixed(1);
    const formatDays = num => (num / ONE_DAY_OF_MS).toFixed(1);
    console.log(
      "🤔 Invalid response! (from Apollo cache?) ...dispatching anyway...",
      {
        responseHours: (responseDuration / 3600000).toFixed(2) + "h",
        queryHours: (queryDuration / 3600000).toFixed(2) + "h",
        "responseDuration - queryDuration": `${
          durationDifference < 0 ? "⚠😅" : ""
        }${formatMinutes(durationDifference)} minutes (${formatDays(
          durationDifference
        )} days)`,
        "query ge":
          new Date(Number(queryVariables.ge)) + " " + queryVariables.ge,
        "query lt":
          new Date(Number(queryVariables.lt)) + " " + queryVariables.lt,
        "response ge":
          new Date(Number(rollup.range.ge)) + " " + rollup.range.ge,
        "response lt":
          new Date(Number(rollup.range.lt)) + " " + rollup.range.lt,
        queryDuration,
        responseDuration,
        machemetrySourceName: queryVariables.machemetrySourceName,
      }
    );
  }
  // TECHDEBT not sure why above is still happening or if it's relevant
  return true;
}
